import CardLayerActions from "@components/CardLayerActions";
import { CheckListIcon } from "@components/Icons";
import { useUserState } from "@context/UserContext";
import {
  Grid,
  TextField,
  createStyles,
  makeStyles,
  useMediaQuery,
  useTheme,
  Typography,
  CircularProgress,
  Button,
  Box,
} from "@material-ui/core";
import { useFormikContext } from "formik";
import React from "react";
import { useReclamationContext } from "../../../context/ReclamationContext/ReclamationUserContext";
import CalendarRange from "@components/CalendarRange";
import { IQueryFilterReclamation } from "@pages/User/ReclamationConsult/models/IQueryFilterReclamation";
import DropdownSelectAll from "@components/DropdownSelectAll";
import { startOfMonth } from "date-fns";

export enum FormField {
  refId = "refId",
  cnpj = "cnpj",
  dateRange = "dateRange",
  startDate = "startDate",
  endDate = "endDate",
}

const FormQueryFilterReclamation: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const isTablet = useMediaQuery(theme.breakpoints.only("sm"));
  const screenSize = isMobile || isTablet;
  const userState = useUserState();
  const {
    loadingDataFilterReclamation,
    setRangeDate,
  } = useReclamationContext();

  const {
    values,
    setFieldValue,
    setFieldTouched,
    submitForm,
  } = useFormikContext<IQueryFilterReclamation>();

  const userCNPJList = userState.listCNPJ
    .map((ele) => ele.BpID || ele.CNPJ)
    .filter((cnpj): cnpj is string => !!cnpj);

  const cnpjOptions = userState.listCNPJ.map((ele) => ({
    value: ele.BpID ? ele.BpID : ele.CNPJ,
    label: ele.companyName,
  }));

  const handleOnChangeIdReclamation = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFieldValue(FormField.refId, event.target.value.replace(/\D/g, ""));
  };

  const handleClearFilters = () => {
    const correctDateRange = [startOfMonth(new Date()), new Date()];
    setFieldValue(FormField.refId, "");
    setFieldValue(FormField.cnpj, userCNPJList);
    setFieldValue(FormField.dateRange, correctDateRange);
    setRangeDate(correctDateRange);
    submitForm();
  };

  const isDateRangeValid = (
    dateRange: [Date | null, Date | null] | null | undefined
  ): boolean => {
    return !!dateRange && !!dateRange[0] && !!dateRange[1];
  };

  const isButtonDisabled =
    !values.cnpj?.length || !isDateRangeValid(values.dateRange);

  return (
    <Grid container xs={12} style={{ padding: "2.9rem" }}>
      <Grid xs={6}>
        <Typography
          style={{
            marginBottom: "1.4rem",
            color: "#3E3D3D",
            fontFamily: "Montserrat",
            fontStyle: "normal",
            fontWeight: "700",
            fontSize: screenSize ? "2rem" : isTablet ? "2.5rem" : "2.4rem",
            marginLeft: screenSize ? "10px" : "0px",
          }}
        >
          Registro de atendimento ao cliente
        </Typography>
      </Grid>
      <CardLayerActions title="Registros de atendimento" icon={CheckListIcon}>
        <Grid container>
          <Grid
            item
            xs={12}
            md={4}
            style={{
              marginBottom: screenSize ? "25px" : "",
              height: "78px",
            }}
          >
            <TextField
              style={{
                width: screenSize ? "100%" : "calc(100% - 30px)",
              }}
              className={classes.textfield}
              label="ID do registro"
              variant="outlined"
              id="refId"
              type="text"
              value={values.refId}
              onChange={handleOnChangeIdReclamation}
              onBlur={() => setFieldTouched(FormField.refId, true)}
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
            style={{
              marginBottom: screenSize ? "25px" : "",
              height: "78px",
            }}
          >
            <Box
              style={{
                width: screenSize ? "100%" : "calc(100% - 30px)",
              }}
            >
              <DropdownSelectAll
                label="Razão Social"
                options={cnpjOptions}
                value={values.cnpj}
                onChange={(selectedValues) => {
                  setFieldValue(FormField.cnpj, selectedValues);
                }}
                isCNPJ={true}
                isSubTitle={true}
              />
              {!values.cnpj?.length && (
                <Typography
                  style={{
                    color: "red",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  Selecione um CNPJ
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid
            md={4}
            xs={12}
            style={{
              marginBottom: screenSize ? "25px" : "",
            }}
          >
            <CalendarRange
              //@ts-ignore
              name="dateRange"
              label="Data ou período"
              value={values.dateRange}
              onChange={(dateRange) => {
                setFieldValue(FormField.dateRange, dateRange);
                setFieldTouched(FormField.startDate, true);
                setFieldTouched(FormField.endDate, true);
                setRangeDate(
                  dateRange?.map((cont) => (cont ? cont : new Date())) ?? []
                );
              }}
              onBlur={() => {
                setFieldTouched(FormField.startDate, true);
                setFieldTouched(FormField.endDate, true);
              }}
            />
          </Grid>

          <Grid
            item
            xs={12}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <Button
              color="primary"
              variant="outlined"
              className={classes.styleButton}
              onClick={handleClearFilters}
            >
              Limpar Filtros
            </Button>
            <Button
              disabled={isButtonDisabled}
              color="primary"
              variant="contained"
              className={classes.styleButtonContained}
              onClick={submitForm}
              startIcon={
                loadingDataFilterReclamation && (
                  <CircularProgress
                    color="secondary"
                    style={{ height: "2rem", width: "2rem" }}
                  />
                )
              }
            >
              {loadingDataFilterReclamation ? "Consultando..." : "Consultar"}
            </Button>
          </Grid>
        </Grid>
      </CardLayerActions>
    </Grid>
  );
};

export { FormQueryFilterReclamation };

const useStyles = makeStyles(() =>
  createStyles({
    title: {
      color: "#3E3D3D",
      fontFamily: "Montserrat",
      fontStyle: "normal",
      fontWeight: "bold",
      lineHeight: "normal",
      paddingTop: "20px",
    },
    inputLabel: {
      position: "absolute",
      color: "#3E3D3D",
      fontFamily: "Montserrat",
      fontSize: "1.6rem",
      backgroundColor: "#fff",
      paddingLeft: "0.2rem",
      paddingRight: "0.8rem",
      marginLeft: "0rem",
      width: "auto",
    },
    textfield: {
      width: "100%",
      fontFamily: "Montserrat",
      "& label": {
        // Controle de estilo antes de digitar
        color: "#3E3D3D",
        fontWeight: 400,
        fontSize: "1.6rem",
        borderRadius: "8px",
        opacity: "0.6",
      },
      // "& input.Mui-focused": { border: "2px solid blue" }, // Controle de estilo durante digitacao
      "& .MuiInputLabel-shrink": {
        // Controle de estilo apos campo digitado
        fontSize: "1.6rem",
        fontWeight: 700,
        color: "#3E3D3D",
        opacity: 1,
        backgroundColor: "#fff",
        paddingRight: 6,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderRadius: "8px",
        opacity: "0.6",
      },
    },
    itemSelect: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
    },
    itemSelectTitle: {
      fontSize: "1.6rem",
      fontWeight: "bold",
    },
    itemSelectSubTitle: {
      fontSize: "1.2rem",
    },
    customSelect: {
      justifyContent: "center",
      alignItems: "center",
      display: "flex",
      marginTop: "0",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      fontFamily: "Montserrat",
      fontSize: "16px",
      fontWeight: 400,
      paddingLeft: "14px",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(114, 101, 81, 0.20)",
      },

      "& .MuiSvgIcon-root": {
        fill: "#D91F05",
      },

      "&:focus": {
        backgroundColor: "#FFFF",
      },
      borderRadius: "8px",
    },
    formControl: {
      borderRadius: "8px",
      height: "56px",
      width: "100%",
    },
    gridInput: {
      marginRight: "2rem",
      marginBottom: "2rem",
      height: "50px",
    },
    styleButton: {
      textTransform: "none",
      fontWeight: "bold",
      borderRadius: "2px",
      boxShadow: "none",
      "&:hover": {
        background: "#fff",
      },
    },
    styleButtonContained: {
      borderRadius: "2px",
      textTransform: "none",
      fontWeight: "bold",
      background: "#D91F05",
      color: "#ffffff",
      boxShadow: "none",
      "&:hover": {
        background: "#D91F31",
      },
      "&:disabled": {
        background: "#ccc",
        color: "#817c7cff",
      },
    },
  })
);
